import Toast from "components/cms/Toast";
import SectionCard from "components/cms/SectionCard";
import {
  AreaInput,
  CardRow,
  FieldRow,
  FileInput,
  GroupBox,
  StringList,
  TextInput,
} from "components/cms/fields";
import useCmsPage from "hooks/useCmsPage";
import {
  getServicePage,
  prepareServicePage,
  updateServicePage,
} from "api/servicepage";
import { emptyServices } from "utils/defaults";
import LogosSection from "components/cms/LogosSection";

const featureCard = (label, path, form, setField, frMap) => (
  <GroupBox title={label}>
    <TextInput
      label="Title"
      path={`${path}.title`}
      form={form}
      setField={setField}
      frMap={frMap}
    />
    <AreaInput
      label="Description"
      path={`${path}.description`}
      form={form}
      setField={setField}
      frMap={frMap}
    />
  </GroupBox>
);

const OFFERINGS = [
  "offering1",
  "offering2",
  "offering3",
  "offering4",
  "offering5",
  "offering6",
];
const PROCESS_STEPS = [
  "processStep1",
  "processStep2",
  "processStep3",
  "processStep4",
  "processStep5",
  "processStep6",
];
const TECH_GROUPS = [
  "techGroup1",
  "techGroup2",
  "techGroup3",
  "techGroup4",
  "techGroup5",
  "techGroup6",
  "techGroup7",
  "techGroup8",
  "techGroup9",
];

const HERO_KEYS = [
  "heroTag",
  "heroTitle1",
  "heroTitle2",
  "heroDescription",
  "heroPrimaryCtaText",
  "heroPrimaryCtaLink",
  "heroSecondaryCtaText",
  "heroSecondaryCtaLink",
  "heroTrustText",
  "heroLogoCard",
  "heroCard1",
  "heroCard2",
  "heroSectionHide",
];

const OFFERING_KEYS = [
  "offeringsTag",
  "offeringsTitle1",
  "offeringsTitle2",
  "offeringsSubtitle",
  ...OFFERINGS,
  "offeringsHide",
];

const PROCESS_KEYS = [
  "processTag",
  "processTitle1",
  "processTitle2",
  "processSubtitle",
  ...PROCESS_STEPS,
  "processHide",
];

const TECH_KEYS = [
  "techTag",
  "techTitle1",
  "techTitle2",
  "techSubtitle",
  ...TECH_GROUPS,
  "techHide",
];

export default function ServicesPage() {
  const cms = useCmsPage({
    load: getServicePage,
    update: updateServicePage,
    empty: emptyServices,
    mapResponse: (res) => res.data?.data,
    prepare: prepareServicePage,
  });
  const {
    form,
    setForm,
    setField,
    frMap,
    loading,
    toast,
    setToast,
    saveSection,
    savingSection,
    isSectionDirty,
  } = cms;

  if (loading) {
    return <p className="mt-5 text-gray-600">Loading Services…</p>;
  }

  return (
    <div className="mt-3">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <SectionCard
        title="Hero"
        hide={form.heroSectionHide}
        hideId="services-heroSectionHide"
        onHideChange={(value) => setField("heroSectionHide", value)}
        onSave={() => saveSection("hero", HERO_KEYS)}
        saving={savingSection === "hero"}
        canSave={isSectionDirty(HERO_KEYS)}
      >
        <FieldRow>
          <TextInput label="Tag" path="heroTag" form={form} setField={setField} frMap={frMap} />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Title line 1"
            path="heroTitle1"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Title line 2"
            path="heroTitle2"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <AreaInput
          label="Description"
          path="heroDescription"
          form={form}
          setField={setField}
          frMap={frMap}
        />
        <FieldRow>
          <TextInput
            label="Primary CTA text"
            path="heroPrimaryCtaText"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Primary CTA link"
            path="heroPrimaryCtaLink"
            form={form}
            setField={setField}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Secondary CTA text"
            path="heroSecondaryCtaText"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Secondary CTA link"
            path="heroSecondaryCtaLink"
            form={form}
            setField={setField}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Trust text"
            path="heroTrustText"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <FileInput
          label="Logo card image"
          path="heroLogoCard.image"
          form={form}
          setField={setField}
        />
        <FieldRow>
          <TextInput
            label="Logo card text"
            path="heroLogoCard.text"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <CardRow>
          {featureCard("Hero card 1", "heroCard1", form, setField, frMap)}
          {featureCard("Hero card 2", "heroCard2", form, setField, frMap)}
        </CardRow>
      </SectionCard>

      <SectionCard
        title="Offerings"
        hide={form.offeringsHide}
        hideId="offeringsHide"
        onHideChange={(value) => setField("offeringsHide", value)}
        onSave={() => saveSection("offerings", OFFERING_KEYS)}
        saving={savingSection === "offerings"}
        canSave={isSectionDirty(OFFERING_KEYS)}
      >
        <FieldRow>
          <TextInput
            label="Tag"
            path="offeringsTag"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Title line 1"
            path="offeringsTitle1"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Title line 2"
            path="offeringsTitle2"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <AreaInput
          label="Subtitle"
          path="offeringsSubtitle"
          form={form}
          setField={setField}
          frMap={frMap}
        />
        <CardRow>
          {OFFERINGS.map((key, index) => (
            <GroupBox key={key} title={`Offering ${index + 1}`}>
              <TextInput
                label="Label"
                path={`${key}.label`}
                form={form}
                setField={setField}
              />
              <TextInput
                label="Title"
                path={`${key}.title`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <AreaInput
                label="Description"
                path={`${key}.description`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
            </GroupBox>
          ))}
        </CardRow>
      </SectionCard>

      <LogosSection />

      <SectionCard
        title="Process"
        hide={form.processHide}
        hideId="services-processHide"
        onHideChange={(value) => setField("processHide", value)}
        onSave={() => saveSection("process", PROCESS_KEYS)}
        saving={savingSection === "process"}
        canSave={isSectionDirty(PROCESS_KEYS)}
      >
        <FieldRow>
          <TextInput
            label="Tag"
            path="processTag"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Title line 1"
            path="processTitle1"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Title line 2"
            path="processTitle2"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <AreaInput
          label="Subtitle"
          path="processSubtitle"
          form={form}
          setField={setField}
          frMap={frMap}
        />
        <CardRow>
          {PROCESS_STEPS.map((key, index) => (
            <GroupBox key={key} title={`Process step ${index + 1}`}>
              <TextInput
                label="Step"
                path={`${key}.step`}
                form={form}
                setField={setField}
              />
              <TextInput
                label="Time"
                path={`${key}.time`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <TextInput
                label="Title"
                path={`${key}.title`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <AreaInput
                label="Description"
                path={`${key}.description`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <StringList
                label="Tags"
                inline
                items={form[key]?.tags}
                onChange={(tags) =>
                  setForm((prev) => ({
                    ...prev,
                    [key]: { ...prev[key], tags },
                  }))
                }
              />
            </GroupBox>
          ))}
        </CardRow>
      </SectionCard>

      <SectionCard
        title="Technologies"
        hide={form.techHide}
        hideId="techHide"
        onHideChange={(value) => setField("techHide", value)}
        onSave={() => saveSection("tech", TECH_KEYS)}
        saving={savingSection === "tech"}
        canSave={isSectionDirty(TECH_KEYS)}
      >
        <FieldRow>
          <TextInput label="Tag" path="techTag" form={form} setField={setField} frMap={frMap} />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Title line 1"
            path="techTitle1"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Title line 2"
            path="techTitle2"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <AreaInput
          label="Subtitle"
          path="techSubtitle"
          form={form}
          setField={setField}
          frMap={frMap}
        />
        <CardRow cols="sm:grid-cols-2 xl:grid-cols-4">
          {TECH_GROUPS.map((key, index) => (
            <GroupBox key={key} title={`Tech group ${index + 1}`}>
              <TextInput
                label="Title"
                path={`${key}.title`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <StringList
                label="Items"
                items={form[key]?.items}
                onChange={(items) =>
                  setForm((prev) => ({
                    ...prev,
                    [key]: { ...prev[key], items },
                  }))
                }
              />
            </GroupBox>
          ))}
        </CardRow>
      </SectionCard>
    </div>
  );
}
